import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { GripVertical } from "lucide-react";
import api from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";

const STATUS_COLOR = {
  published: "bg-emerald/15 text-emerald border-emerald/30",
  awaiting_review: "bg-amber/15 text-amber border-amber/30",
  draft: "bg-cyan/15 text-cyan border-cyan/30",
  scheduled: "bg-crimson/15 text-crimson border-crimson/30",
};

export default function Calendar() {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [dragId, setDragId] = useState(null);
  const [overKey, setOverKey] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/calendar").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const y = month.getFullYear(), m = month.getMonth();
  const first = new Date(y, m, 1);
  const startDay = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dateKey = (a) => (a.scheduled_for || a.publication_date || a.created_at || "").slice(0, 10);
  const cellKey = (d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const itemsFor = (d) => items.filter((a) => dateKey(a) === cellKey(d));

  const draggable = (a) => a.status !== "published"; // published items are live; don't reschedule via drag

  const onDrop = async (d) => {
    setOverKey(null);
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const art = items.find((a) => a.id === id);
    if (!art || !draggable(art)) return;
    if (dateKey(art) === cellKey(d)) return;
    const scheduled_for = `${cellKey(d)}T09:00:00Z`;
    // optimistic update
    setItems((prev) => prev.map((a) => a.id === id ? { ...a, status: "scheduled", scheduled_for } : a));
    try {
      await api.post(`/admin/articles/${id}/status`, { status: "scheduled", scheduled_for });
      toast.success(`Scheduled for ${cellKey(d)}`);
      load();
    } catch { toast.error("Reschedule failed"); load(); }
  };

  return (
    <AdminLayout title="Content Calendar">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-4 text-xs items-center flex-wrap">
          {Object.entries(STATUS_COLOR).map(([k, c]) => <span key={k} className={`px-2 py-0.5 border ${c} uppercase tracking-wider`}>{k.replace("_", " ")}</span>)}
          <span className="text-admmuted">Drag a draft, review or scheduled item onto a day to schedule it.</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setMonth(new Date(y, m - 1, 1))} data-testid="cal-prev" className="text-admtext2 hover:text-admtext px-2">‹</button>
          <span className="font-serif text-lg text-admtext">{month.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</span>
          <button onClick={() => setMonth(new Date(y, m + 1, 1))} data-testid="cal-next" className="text-admtext2 hover:text-admtext px-2">›</button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-admborder border border-admborder">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="bg-admbg text-admmuted text-xs uppercase tracking-wider px-2 py-2 text-center">{d}</div>)}
        {cells.map((d, i) => (
          <div
            key={i}
            data-testid={d ? `cal-cell-${cellKey(d)}` : undefined}
            onDragOver={(e) => { if (d && dragId) { e.preventDefault(); setOverKey(cellKey(d)); } }}
            onDragLeave={() => { if (d && overKey === cellKey(d)) setOverKey(null); }}
            onDrop={(e) => { if (d) { e.preventDefault(); onDrop(d); } }}
            className={`bg-admcard min-h-[110px] p-1.5 transition-colors ${d && overKey === cellKey(d) ? "ring-2 ring-cyan ring-inset bg-cyan/5" : ""}`}
          >
            {d && <div className="text-xs text-admmuted font-mono mb-1">{d}</div>}
            {d && itemsFor(d).map((a) => (
              <div
                key={a.id}
                draggable={draggable(a)}
                onDragStart={(e) => { if (draggable(a)) { setDragId(a.id); e.dataTransfer.effectAllowed = "move"; } else { e.preventDefault(); } }}
                onDragEnd={() => { setDragId(null); setOverKey(null); }}
                data-testid={`cal-item-${a.id}`}
                className={`group flex items-center gap-1 text-[10px] px-1.5 py-1 mb-1 border ${STATUS_COLOR[a.status] || "text-admtext2 border-admborder"} ${draggable(a) ? "cursor-grab active:cursor-grabbing" : ""} ${dragId === a.id ? "opacity-40" : ""}`}
              >
                {draggable(a) && <GripVertical size={10} className="opacity-40 group-hover:opacity-80 shrink-0" />}
                <Link to={`/admin/articles/${a.id}`} className="truncate hover:underline" onClick={(e) => e.stopPropagation()}>{a.headline}</Link>
              </div>
            ))}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
